import { useState } from "react";
import type { ParseKeys } from "i18next";
import { cx } from "./ui";
import { useTranslation } from "react-i18next";


/**
 * The programme over time: signups, conversions, and what was earned.
 *
 * Three series and one chart rather than three charts, because the question
 * being asked of it is about the *gaps* — a week where signups climbed and
 * conversions did not is the shape worth seeing, and it is invisible when the
 * two lines live in separate panels with separate scales.
 *
 * Drawn as an inline SVG polyline. `ClickChart` next door uses divs, which is
 * right for one series of bars and wrong here: three overlapping series need
 * sub-pixel positioning and a shared coordinate space, and a charting library
 * would be ~100KB to draw ninety points.
 *
 * ---------------------------------------------------------------------------
 * Two scales, deliberately
 * ---------------------------------------------------------------------------
 * Counts and money do not share an axis. Ten signups and ten dollars are not
 * the same height, and putting them on one scale would draw a truthful line
 * that means nothing. So each series is normalised against its own peak and
 * the chart carries no y-axis numbers at all — what it shows is *shape*, and
 * the figures are in the legend and the tooltip, where they are exact.
 */

/**
 * One line: where to read it from a point, and how to say its peak.
 *
 * The chart is told its series rather than knowing them, because two callers
 * want different ones — the programme sees signups, conversions and earnings,
 * and an affiliate also sees clicks, which for them is the top of the funnel
 * and the only part they can directly change.
 */
export interface Series<T> {
  key: string;
  /** A key under the `charts` namespace, read through `t` where it is drawn. */
  label: ParseKeys<"charts">;
  stroke: string;
  value: (point: T) => number;
  /** How the peak and the readout render. Money and counts are not the same. */
  format: (value: number) => string;
}

/** A point on any of these charts: a day, and whatever the series read off it. */
export interface DayPoint {
  day: string;
}

/* A viewBox rather than pixels: the SVG scales to whatever width the card is,
   and the geometry below never has to know it. */
const W = 720;
const H = 180;
const PAD = 6;

export function TrendChart<T extends DayPoint>({
  points,
  series,
}: {
  points: T[];
  series: Series<T>[];
}) {
  /*
   * Which day the reader is pointing at, as an index rather than a date. The
   * whole chart is one pointer target with a computed index — a hit area per
   * day would be ninety elements and would still miss between them.
   */
  const { t } = useTranslation("charts");
  const [at, setAt] = useState<number | null>(null);

  if (points.length === 0) return null;

  /*
   * Each line against its own peak. Ten signups and ten dollars are not the
   * same height, and one shared scale would draw a truthful line that means
   * nothing — see the note above.
   *
   * Two functions and not one, which looks redundant and is not. The *divisor*
   * is guarded at 1 so an all-zero series divides rather than producing NaN;
   * the *label* is the real maximum. Sharing the guarded value made the legend
   * read "peak 1" beside a line flat on the axis — a claim that something
   * happened on a chart showing that nothing did.
   */
  const peakOf = (line: Series<T>) => Math.max(1, ...points.map(line.value));
  const maxOf = (line: Series<T>) => Math.max(0, ...points.map(line.value));
  const empty = series.every((line) => points.every((point) => line.value(point) === 0));

  const x = (index: number) =>
    points.length === 1 ? W / 2 : PAD + (index / (points.length - 1)) * (W - PAD * 2);
  const y = (value: number, peak: number) => H - PAD - (value / peak) * (H - PAD * 2);

  const path = (line: Series<T>) => {
    const peak = peakOf(line);
    return points
      .map((point, index) => `${index === 0 ? "M" : "L"}${x(index)} ${y(line.value(point), peak)}`)
      .join(" ");
  };

  const active = at === null ? null : points[at];

  return (
    <div className="px-4 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {series.map((line) => (
          <span key={line.key} className="inline-flex items-center gap-1.5 text-[12px] text-fg-muted">
            <span
              className="inline-block h-0.5 w-4 rounded-full"
              style={{ backgroundColor: line.stroke }}
              aria-hidden="true"
            />
            {t(line.label)}
            {/* The peak beside the label is what stands in for a y-axis. Without
                it the reader has a shape and no magnitude at all. */}
            <span className="text-fg-subtle">peak {line.format(maxOf(line))}</span>
          </span>
        ))}
      </div>

      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="block h-44 w-full touch-none border-b border-line"
          preserveAspectRatio="none"
          role="img"
          aria-label={t("trendLabel", { series: series.map((line) => t(line.label)).join(", "), count: points.length })}
          onPointerMove={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            const ratio = (event.clientX - box.left) / box.width;
            setAt(Math.min(points.length - 1, Math.max(0, Math.round(ratio * (points.length - 1)))));
          }}
          onPointerLeave={() => setAt(null)}
        >
          {/*
           * `vector-effect` on every stroke. Without it the non-uniform scale
           * from `preserveAspectRatio="none"` stretches the stroke width with
           * the geometry, and a 30-day chart draws visibly fatter lines than a
           * 90-day one on the same card.
           */}
          {at !== null && (
            <line
              x1={x(at)}
              x2={x(at)}
              y1={PAD}
              y2={H - PAD}
              stroke="var(--color-line-strong)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          )}

          {series.map((line) => (
            <path
              key={line.key}
              d={path(line)}
              fill="none"
              stroke={line.stroke}
              strokeWidth="1.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
              /* A flat zero series would otherwise draw three overlapping lines
                 along the bottom edge and read as a bug. */
              opacity={empty ? 0.25 : 1}
            />
          ))}

          {at !== null &&
            series.map((line) => (
              <circle
                key={line.key}
                cx={x(at)}
                cy={y(line.value(points[at]!), peakOf(line))}
                r="3"
                fill="var(--color-surface)"
                stroke={line.stroke}
                strokeWidth="1.5"
                vectorEffect="non-scaling-stroke"
              />
            ))}
        </svg>

        {/*
         * The readout sits above the chart rather than following the pointer.
         * A tooltip that tracks the cursor has to flip near both edges and is
         * unreachable on a touch screen, where there is no hover — here a tap
         * sets the index and the panel simply updates in place.
         */}
        <div
          className={cx(
            "pointer-events-none absolute top-0 right-0 rounded-md border border-line bg-surface/95 px-2.5 py-1.5 text-[12px] shadow-xs backdrop-blur transition-opacity duration-200 ease-smooth",
            active ? "opacity-100" : "opacity-0",
          )}
          aria-live="polite"
        >
          <p className="font-medium text-fg">{active?.day ?? ""}</p>
          {/* Every series, so the readout says the same thing the legend
              promises rather than a hard-coded three of them. */}
          {series.map((line) => (
            <p key={line.key} className="text-fg-muted">
              {line.format(active ? line.value(active) : 0)} {t(line.label).toLowerCase()}
            </p>
          ))}
        </div>
      </div>

      <div className="mt-2 flex justify-between text-[11px] text-fg-subtle">
        <span>{points[0]?.day ?? ""}</span>
        <span>{points.at(-1)?.day ?? ""}</span>
      </div>
    </div>
  );
}
